import React, { useState } from 'react';

import {
  alpha,
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputBase,
  InputLabel,
  Stack,
  styled
} from '@mui/material';

import { useFieldDescription } from './fieldDescription';
import { sharedClasses } from './sharedClasses';
import { SCOUT_BLUE, WHITE } from '../../../ThemeContext/constants';
import { theme } from '../../../ThemeContext/ThemeObject';
import DebounceInput from '../Utilities/DebounceInput';

import type { ChangeEvent, CSSProperties, Dispatch, FocusEventHandler, RefObject } from 'react';

interface IStyle {
  [className: string]: CSSProperties | IStyle;
}

interface IFieldRef {
  innerRef?: RefObject<HTMLInputElement> | ((el: HTMLDivElement) => void);
}

interface IFormTextFieldProps extends IFieldRef {
  label?: string;
  ariaLabel?: string;
  value: string | number;
  onChange: Dispatch<ChangeEvent<HTMLInputElement>>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  required?: boolean;
  error?: string;
  id?: string;
  styles?: CSSProperties | IStyle;
  draggable?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  dataTestId?: string;
  maxLength?: number;
  placeholder?: string;
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  noWrapError?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  errorSx?: object;
}

interface IFormNumberFieldProps extends IFieldRef {
  label?: string;
  value: string | number;
  onChange: Dispatch<ChangeEvent<HTMLInputElement>>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  required?: boolean;
  error?: string;
  id?: string;
  styles?: CSSProperties | IStyle;
  fullWidth?: boolean;
  dataTestId?: string;
  placeholder?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  unit?: string;
  disabled?: boolean;
}

interface IMultilineTextFieldProps extends IFieldRef {
  label?: string;
  value: string | number;
  onChange: Dispatch<ChangeEvent<HTMLInputElement>>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  required?: boolean;
  error?: string;
  id?: string;
  styles?: CSSProperties | IStyle;
  rows?: number;
  helperText?: string;
  draggable?: boolean;
  dataTestId?: string;
  fullWidth?: boolean;
  placeholder?: string;
  maxLength?: number;
  chips?: string[];
  onChipClick?: (chip: string) => void;
  showChipsCallback?: (showChips: boolean) => void;
  disableResize?: boolean;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  name?: string;
  ref?: Ref<HTMLInputElement | HTMLTextAreaElement>;
}

interface IDebouncedFormTextFieldProps extends IFieldRef {
  label?: string;
  onDebouncedChange: (value: string | number) => void;
  debounceTime?: number;
  defaultValue?: string;
  required?: boolean;
  error?: string;
  id?: string;
  styles?: CSSProperties | IStyle;
  helperText?: string;
  fullWidth?: boolean;
  dataTestId?: string;
  maxLength?: number;
}

interface IDebouncedFormNumberField extends IFieldRef {
  label?: string;
  onDebouncedChange: (value: string | number) => void;
  debounceTime?: number;
  defaultValue?: number;
  required?: boolean;
  error?: string;
  id?: string;
  styles?: CSSProperties | IStyle;
  fullWidth?: boolean;
  dataTestId?: string;
  unit?: string;
}

const writeRef = (
  target: Ref<HTMLInputElement | HTMLTextAreaElement> | IFieldRef['innerRef'],
  node: HTMLInputElement | null
) => {
  if (!target) return;
  if (typeof target === 'function') {
    target(node);
    return;
  }
  Object.assign(target, { current: node });
};

const TextInput = styled(InputBase, {
  shouldForwardProp: (prop) => prop !== 'disableResize'
})<{
  disableResize?: boolean;
  error?: boolean;
  fullWidth?: boolean;
}>(({ disableResize, error, fullWidth }) => ({
  width: fullWidth ? '100%' : 'auto',
  'label + &': {
    marginTop: '24px'
  },
  '& .MuiInputBase-input': {
    borderRadius: '6px',
    position: 'relative',
    backgroundColor: WHITE,
    color: '#333333',
    border: `1px solid ${error ? '#CF726F' : '#DDDDDD'}`,
    fontSize: '16px',
    padding: '6px 10px',
    margin: '3px 0',
    fontFamily: 'Source Sans Pro, sans-serif',
    transition: 'all 0.16s ease-in-out',
    '&::placeholder': {
      color: '#B9B9B9',
      fontFamily: 'Source Sans Pro, sans-serif',
      opacity: 1
    },
    '&:focus': {
      boxShadow: error
        ? `${alpha('#E37D7A', 0.25)} 0 0 0 2px`
        : `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 2px`,
      borderColor: error ? '#E37D7A' : theme.palette.primary.main
    },
    '&.MuiInputBase-inputMultiline': {
      transition: 'border-color 0.16s ease-in-out, box-shadow 0.16s ease-in-out',
      resize: disableResize ? 'none' : 'vertical',
      overflowY: 'auto',
      willChange: 'height',
      minHeight: '1.5em'
    }
  }
}));

export const FormTextField = ({
  label,
  ariaLabel,
  value,
  onChange,
  onBlur,
  required,
  error,
  id,
  innerRef,
  styles,
  draggable,
  helperText,
  fullWidth = false,
  dataTestId,
  maxLength,
  placeholder,
  onFocus,
  noWrapError,
  onKeyDown,
  errorSx
}: IFormTextFieldProps) => {
  const field = useFieldDescription(id, error, helperText);

  return (
    <FormControl sx={{ width: fullWidth ? '100%' : undefined }} variant="standard">
      {label ? (
        <InputLabel shrink htmlFor={field.controlId} sx={sharedClasses.inputLabel}>
          {label}
          {required ? <span>*</span> : null}
        </InputLabel>
      ) : null}
      <TextInput
        disabled={draggable}
        error={!!error}
        fullWidth={fullWidth}
        id={field.controlId}
        inputRef={innerRef}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        sx={styles}
        value={value}
        inputProps={{
          'aria-describedby': field.describedBy,
          'aria-label': ariaLabel,
          'data-testid': dataTestId,
          maxLength
        }}
      />
      {error ? (
        <Box
          id={field.errorId}
          sx={{ ...sharedClasses.errorBox, textWrap: noWrapError ? 'nowrap' : 'wrap', ...errorSx }}
        >
          {error}
        </Box>
      ) : null}
      {helperText ? (
        <FormHelperText
          id={field.helperId}
          sx={{ marginLeft: '4px', fontFamily: 'Source Sans Pro' }}
        >
          {helperText}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
};

export const FormNumberField = ({
  label,
  value,
  onChange,
  onBlur,
  required,
  error,
  id,
  innerRef,
  styles,
  unit,
  fullWidth,
  dataTestId,
  onKeyDown,
  placeholder,
  disabled
}: IFormNumberFieldProps) => {
  const field = useFieldDescription(id, error);

  return (
    <FormControl sx={{ width: '100%' }} variant="standard">
      {label ? (
        <InputLabel shrink htmlFor={field.controlId} sx={sharedClasses.inputLabel}>
          {label}
          {required ? <span>*</span> : null}
        </InputLabel>
      ) : null}
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          marginTop: 3,
          columnGap: 1,
          color: '#838383'
        }}
      >
        <TextInput
          disabled={disabled}
          fullWidth={fullWidth}
          id={field.controlId}
          inputRef={innerRef}
          onBlur={onBlur}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          sx={styles}
          type="number"
          value={value}
          inputProps={{
            'aria-describedby': field.describedBy,
            'aria-invalid': field.invalid || undefined,
            'data-testid': dataTestId
          }}
        />
        {unit}
      </Stack>
      {error ? (
        <Box id={field.errorId} sx={sharedClasses.errorBox}>
          {error}
        </Box>
      ) : null}
    </FormControl>
  );
};

export const DebouncedFormTextField = ({
  label,
  defaultValue,
  onDebouncedChange,
  required,
  error,
  debounceTime,
  id,
  innerRef,
  styles,
  helperText,
  fullWidth = false,
  dataTestId,
  maxLength
}: IDebouncedFormTextFieldProps) => (
  <DebounceInput
    debounceTime={debounceTime}
    defaultValue={defaultValue}
    onDebouncedChange={onDebouncedChange}
  >
    {({ value, onChange }) => (
      <FormTextField
        dataTestId={dataTestId}
        error={error}
        fullWidth={fullWidth}
        helperText={helperText}
        id={id}
        innerRef={innerRef}
        label={label}
        maxLength={maxLength}
        onChange={onChange}
        required={required}
        styles={styles}
        value={value}
      />
    )}
  </DebounceInput>
);

export const DebouncedFormNumberField = ({
  label,
  defaultValue,
  onDebouncedChange,
  required,
  error,
  debounceTime,
  id,
  innerRef,
  styles,
  dataTestId,
  unit,
  fullWidth
}: IDebouncedFormNumberField) => (
  <DebounceInput
    debounceTime={debounceTime}
    defaultValue={defaultValue}
    onDebouncedChange={onDebouncedChange}
  >
    {({ value, onChange }) => (
      <FormNumberField
        dataTestId={dataTestId}
        error={error}
        fullWidth={fullWidth}
        id={id}
        innerRef={innerRef}
        label={label}
        onChange={onChange}
        required={required}
        styles={styles}
        unit={unit}
        value={value}
      />
    )}
  </DebounceInput>
);

export const MultilineFormTextField = ({
  label,
  value,
  onChange,
  required,
  error,
  id,
  innerRef,
  styles,
  rows,
  helperText,
  draggable = false,
  dataTestId,
  fullWidth = false,
  placeholder,
  maxLength,
  chips,
  onChipClick,
  showChipsCallback,
  disableResize = false,
  onBlur,
  onKeyDown,
  name,
  ref,
  ...rest
}: IMultilineTextFieldProps) => {
  const field = useFieldDescription(id, error, helperText);
  const [showChips, setShowChips] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const shouldShowChips = showChips && (!value || !isFocused);

  return (
    <FormControl sx={{ width: fullWidth ? '100%' : undefined }} variant="standard">
      {label ? (
        <InputLabel shrink htmlFor={field.controlId} sx={sharedClasses.inputLabel}>
          {label}
          {required ? <span>*</span> : null}
        </InputLabel>
      ) : null}
      <Box
        sx={{
          position: 'relative',
          marginTop: label ? theme.spacing(3) : undefined
        }}
      >
        <TextInput
          {...rest}
          fullWidth
          multiline
          disabled={draggable}
          disableResize={disableResize}
          id={field.controlId}
          name={name}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          required={required}
          rows={rows ?? 6}
          value={value}
          inputProps={{
            'aria-describedby': field.describedBy,
            'aria-invalid': field.invalid || undefined,
            'data-testid': dataTestId,
            maxLength
          }}
          inputRef={(node) => {
            writeRef(innerRef, node);
            writeRef(ref, node);
          }}
          onBlur={(event) => {
            onBlur?.(event);
            setTimeout(() => {
              setIsFocused(false);
              showChipsCallback?.(false);
              setShowChips(false);
            }, 400);
          }}
          onFocus={() => {
            if (chips?.length) {
              setIsFocused(true);
              setShowChips(true);
              showChipsCallback?.(true);
            }
          }}
          sx={{
            ...styles,
            '& .MuiInputBase-input': {
              paddingBottom: shouldShowChips ? '60px' : undefined,
              minHeight: shouldShowChips ? '100px' : 'auto',
              transition: 'padding-bottom 0.3s ease, min-height 0.3s ease'
            }
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            margin: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderBottomLeftRadius: '6px',
            borderBottomRightRadius: '6px',
            zIndex: 1,
            pointerEvents: shouldShowChips ? 'auto' : 'none',
            transform: shouldShowChips ? 'translateY(0)' : 'translateY(10px)',
            opacity: shouldShowChips ? 1 : 0,
            transition: 'transform 0.3s ease, opacity 0.3s ease'
          }}
        >
          {chips?.map((chip) => (
            <Chip
              key={chip}
              label={chip}
              onClick={() => onChipClick?.(chip)}
              size="small"
              sx={{
                backgroundColor: '#EDF2F5',
                color: SCOUT_BLUE,
                '&:hover': {
                  backgroundColor: '#E0E7EB'
                },
                cursor: 'pointer'
              }}
            />
          ))}
        </Box>
      </Box>
      {error ? (
        <Box id={field.errorId} sx={sharedClasses.errorBox}>
          {error}
        </Box>
      ) : null}
      {helperText ? (
        <FormHelperText id={field.helperId} sx={{ marginLeft: '4px' }}>
          {helperText}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
};
